import { createContext, useContext, useState } from "react"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { animated, useTransition } from "@react-spring/web"

import { cn } from "@/lib/utils"

type DialogContext = { open: boolean; onLeaveAnimationFinish?: () => void }
const DialogContext = createContext<DialogContext>({ open: false })

export const Root = ({
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange: externalOnOpenChange,
  onClose,
  ...props
}: DialogPrimitive.DialogProps & { onClose?: () => void }) => {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <DialogContext.Provider
      value={{
        open: controlledOpen !== undefined ? controlledOpen : open,
        onLeaveAnimationFinish: onClose,
      }}
    >
      <DialogPrimitive.Root
        {...props}
        open={controlledOpen !== undefined ? controlledOpen : open}
        onOpenChange={(value) => {
          externalOnOpenChange?.(value)
          setOpen(value)
        }}
      />
    </DialogContext.Provider>
  )
}

export const Content = ({
  children,
  className,
  overlayClassName,
  position = "right",
  title,
  ...props
}: DialogPrimitive.DialogContentProps & {
  position?: "left" | "right" | "top"
  overlayClassName?: string
}) => {
  const { open, onLeaveAnimationFinish } = useContext(DialogContext)

  const transitionStyles = {
    right: {
      from: {
        opacity: 0,
        transform: "translateX(100%)",
      },
      enter: { opacity: 1, transform: "translateX(0%)" },
      leave: {
        opacity: 0,
        transform: "translateX(100%)",
      },
    },
    left: {
      from: {
        opacity: 0,
        transform: "translateX(-100%)",
      },
      enter: { opacity: 1, transform: "translateX(0%)" },
      leave: {
        opacity: 0,
        transform: "translateX(-100%)",
      },
    },
    top: {
      from: {
        opacity: 0,
        transform: "translateY(-100%)",
      },
      enter: { opacity: 1, transform: "translateY(0%)" },
      leave: {
        opacity: 0,
        transform: "translateY(-100%)",
      },
    },
  }[position]

  const transitions = useTransition(open, {
    ...transitionStyles,
    config: {
      clamp: true,
      tension: 300,
    },
    onDestroyed(value) {
      if (value) onLeaveAnimationFinish?.()
    },
  })

  return (
    <>
      {transitions((styles, item) =>
        item ? (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay
              id="overlay"
              className={cn(
                "fixed inset-0 isolate z-modal-overlay bg-black/50",
                overlayClassName,
              )}
              forceMount
              asChild
            >
              <animated.div style={{ opacity: styles.opacity }} />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content
              forceMount
              asChild
              className={cn(
                "fixed top-0 isolate z-modal h-screen w-fit overflow-auto bg-white md:w-11/12",
                {
                  "left-0": position === "left",
                  "right-0": position === "right",
                  "top-0": position === "top",
                },
                className,
              )}
              onPointerDownOutside={(e) => {
                if (
                  e.currentTarget instanceof HTMLElement &&
                  e.currentTarget.id === "overlay"
                ) {
                  return
                }
                e.preventDefault()
              }}
              {...props}
            >
              <animated.div style={styles}>
                <Title className="sr-only">{title}</Title>
                {children}
              </animated.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        ) : null,
      )}
    </>
  )
}

export const Title = DialogPrimitive.Title
export const Description = DialogPrimitive.Description
export const Trigger = DialogPrimitive.Trigger
export const Close = DialogPrimitive.Close
